import Quill from 'quill'

// Utility function to check if text contains variable syntax
export const isVariableInput = (text: string | undefined | null): boolean => {
  if (!text || typeof text !== 'string') return false
  return text.trim().slice(-2) === '{{'
}

// Helper function to escape dots in ID selectors
export const escapeIdSelector = (selector: string) =>
  // Handle ID selectors by escaping dots in the ID portion
  selector.replace(/#([^[\s]+)/g, (_, id) => `#${id.replace(/\./g, '\\.')}`)

// Utility function to insert node variable at cursor position
export const insertNodeVarAtCursorUtil = (
  nodevar: string,
  inputElementSelector?: string,
  replaceEntireField?: boolean,
  onSelect?: (nodevar: string) => void,
  onComplete?: () => void,
) => {
  const element = document.querySelector(escapeIdSelector(inputElementSelector || ''))
  if (!element) {
    return
  }

  // Try to get Quill instance first
  const found = Quill.find(element)
  const quill = found instanceof Quill ? found : null

  if (quill) {
    // Handle Quill editor
    if (replaceEntireField) {
      // Replace entire content with the nodevar
      quill.setText('')
      quill.insertText(0, nodevar, 'user')
      quill.setSelection(nodevar.length, 0)
    } else {
      // Get current selection
      const range = quill.getSelection()

      // If no selection, try to get the stored cursor position
      let insertIndex = range?.index
      if (insertIndex === undefined) {
        const storedPosition = element.getAttribute('data-cursor-position')
        if (storedPosition) {
          insertIndex = parseInt(storedPosition, 10)
        } else {
          // If no stored position, use the current length
          const length = quill.getLength()
          insertIndex = length > 1 ? length - 1 : 0
        }
      }

      // Remove the last '{{' if present
      const textBeforeCursor = quill.getText(0, insertIndex)
      if (textBeforeCursor.endsWith('{{')) {
        quill.deleteText(insertIndex - 2, 2) // delete only the two braces
        insertIndex -= 2 // Adjust insert index after removing braces
      }

      // Focus the editor before inserting
      quill.focus()

      // Insert the nodevar at cursor position
      quill.insertText(insertIndex, nodevar, 'user')

      // Set selection right after the inserted nodevar
      quill.setSelection(insertIndex + nodevar.length, 0)

      // Force a text-change event to ensure the content is updated
      quill.update()
    }
  } else {
    // Handle regular input/textarea
    const inputElement = element as HTMLInputElement
    const isTextArea = inputElement.tagName === 'TEXTAREA'
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window[isTextArea ? 'HTMLTextAreaElement' : 'HTMLInputElement'].prototype,
      'value',
    )?.set

    if (!nativeInputValueSetter) {
      return
    }

    if (replaceEntireField) {
      nativeInputValueSetter.call(inputElement, nodevar)
      const newPosition = nodevar.length
      inputElement.selectionStart = newPosition
      inputElement.selectionEnd = newPosition
    } else {
      const insertPos = inputElement.selectionStart ?? 0
      const braceBefore = inputElement.value.slice(insertPos - 2, insertPos) === '{{'
      const beforeText = inputElement.value.slice(0, insertPos - (braceBefore ? 2 : 0))
      const afterText = inputElement.value.slice(insertPos)
      const newText = `${beforeText}${nodevar}${afterText}`

      // First update the value
      nativeInputValueSetter.call(inputElement, newText)

      // Then set the cursor position
      const newCursorPos = beforeText.length + nodevar.length
      requestAnimationFrame(() => {
        inputElement.selectionStart = newCursorPos
        inputElement.selectionEnd = newCursorPos
        inputElement.focus()
      })
    }

    // Dispatch a change event to trigger any React state updates
    const event = new Event('input', { bubbles: true })
    inputElement.dispatchEvent(event)
  }

  onSelect?.(nodevar)
  onComplete?.()
}
